import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import Avatar from '../../../../components/Avatar';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import {STUDENT_ROLE_ID} from '../../../../constants/roles';
import LinkBuilder from '../../../../helpers/link-builder';
import {get, post} from '../../../../services/api';

const CandidateStatusForm = props => {
  const {
    params: { candidate, processId },
    actions: { proceed, dismiss, handleError },
  } = props;

  const {
    CandidateID,
    FullName,
    PictureUrl,
    RoleID,
    RoleName,
    FirmName,
    OfficeLocationName,
    currentSchoolName,
  } = candidate;

  const [formData, setFormData] = useState({
    init: false,
    fields: [],
    options: [],
  });

  useEffect(() => {
    let isSubscribed = true;

    get(
      ENDPOINT.FORM.GET.STATUS_PROFILE,
      { id: processId, candidateId: CandidateID },
      {
        handleSuccess: response => {
          if (isSubscribed) {
            const { fields, options, permissions } = response;

            setFormData({
              init: true,
              permissions,
              fields: {
                ...fields.status.reduce((res, item) => {
                  res[item.replace(/\s/g, '__')] = true;
                  return res;
                }, {}),
              },
              options: {
                status: options.status.map(status => ({
                  ...status,
                  name: status.value.replace(/\s/g, '__'),
                })),
              },
            });
          }
        },
      }
    );

    return () => {
      isSubscribed = false;
    };
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    const preparedValues = Object.keys(values)
      .filter(key => !!values[key])
      .map(key => key.replace(/__/g, ' '));

    const postParams = {
      id: processId,
      candidateId: CandidateID,
    };

    if (preparedValues.length > 0) {
      postParams.status = preparedValues;
    }

    post(ENDPOINT.FORM.SAVE.STATUS_PROFILE, postParams, {
      handleForbidden: () =>
        handleError({
          data: {
            message:
              'You do not have permission to change a candidate’s status.',
          },
        }),
      handleError: response => handleError(response),
      handleSuccess: proceed,
      handleComplete: () => setSubmitting(false),
    });
  };

  const picture = LinkBuilder.getCandidatePictureSizeLink(
    PictureUrl,
    '200x200'
  );

  const role =
    RoleID === STUDENT_ROLE_ID
      ? `${RoleName} at ${currentSchoolName}`
      : `${RoleName} at ${FirmName}`;

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner candidate-status-form">
        <Formik initialValues={formData.fields} onSubmit={handleSubmit}>
          {({
            values,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}

              <div className="form__group form__group--compact">
                <div className="form__row">
                  <BasicLabel
                    name="candidateLabel"
                    required={false}
                    text="Set status to this candidate"
                  />
                </div>

                <div className="form__row form__row--split">
                  <div className="form__column">
                    <div className="candidate-status-form__person">
                      <Avatar size={160} name={FullName} image={picture} />
                      <div className="candidate-status-form__name">
                        {FullName}
                      </div>
                      <div className="candidate-status-form__text">{role}</div>
                      <div className="candidate-status-form__text">
                        {OfficeLocationName}
                      </div>
                    </div>
                  </div>

                  <div className="form__column">
                    <div className="candidate-status-form__steps">
                      {formData.options.status.map(status => (
                        <div className="form__row" key={status.name}>
                          <BasicCheckbox
                            disabled={
                              !formData.permissions.ChangeCandidatesStatus
                            }
                            value={status.name}
                            singleRow
                            checked={values[status.name] || false}
                            title={status.label}
                            name={status.name}
                            controlFunc={handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    disabled={!formData.permissions.ChangeCandidatesStatus}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default CandidateStatusForm;
